<template>
  <div class="position-relative">
    <div class="table-wrapper invalidations">
      <div class="table-header">
        <div class="table-header__title">
          <p class="text">
            Invalidating objects removes them from CDN edge caches. A faster and
            less expensive method is to use versioned object or directory names.
          </p>
        </div>
        <div class="d-sm-flex">
          <b-button
            v-if="$can('create', 'invalidations', 'cdn')"
            variant="bg-primary"
            class="mb-1"
            @click="showForm = true"
          >
            <GjIcon
              class="icon-left"
              name="Plus"
            />
            Create Invalidation
          </b-button>
          <b-button
            v-if="$can('get', 'invalidations', 'cdn')"
            variant="bg-white"
            :disabled="!isSelected"
            class="mb-1 d-none d-lg-flex"
            @click="showView = true"
          >
            Details
          </b-button>
          <b-button
            v-if="$can('create', 'invalidations', 'cdn')"
            variant="bg-white"
            :disabled="!isSelected"
            class="mb-1 d-none d-lg-flex"
            @click="onCopyClick"
          >
            Copy
          </b-button>
          <b-button
            v-if="$can('list', 'invalidations', 'cdn')"
            variant="bg-white"
            class="ml-auto mb-1 mr-0"
            @click="onRefresh()"
          >
            <GjIcon
              class="icon-left"
              name="Refresh"
            />
            Refresh
          </b-button>
        </div>
      </div>
      <div
        :class="
          currentBreakPoint === 'lg' || currentBreakPoint === 'xl'
            ? 'default-table'
            : 'mt-2'
        "
      >
        <div class="position-relative d-none d-lg-block">
          <b-table
            ref="table"
            :items="invalidations"
            :fields="fields"
            :class="{ loaded: !loading }"
            select-mode="single"
            show-empty
            selectable
            responsive
            @row-selected="onRowSelected"
          >
            <template #cell(selected)="{ rowSelected }">
              <select-button :selected="rowSelected" />
            </template>

            <template #cell(createdAt)="data">
              {{
                formatOffsetDateTime(
                  data.value.endsWith('Z') ? data.value : `${data.value}Z`,
                )
              }}
            </template>

            <template #cell(completedAt)="data">
              {{
                formatOffsetDateTime(
                  data.value.endsWith('Z') || !data.value
                    ? data.value
                    : `${data.value}Z`,
                )
              }}
            </template>

            <template #empty>
              <div
                v-if="loading"
                class="loading-table"
              />
              <no-data
                v-else
                no-data-text="No invalidations have been added yet..."
                create-text="+ Create Invalidation"
                access-page="Invalidations"
                :has-permissions="hasPermissions"
                @action="showForm = true"
              />
            </template>
          </b-table>
          <table-busy v-if="loading && invalidations.length > 0" />
        </div>

        <div class="position-relative d-lg-none mt-2">
          <div :class="{ 'opacity-7': loading }">
            <template v-for="invalidation in invalidations">
              <card
                :key="invalidation.id"
                :fields="fields"
                :item="invalidation"
                class="top-fade-in"
              >
                <template #options>
                  <b-dropdown
                    id="dropdown-1"
                    right
                    variant="white"
                  >
                    <template #button-content>
                      <GjIcon name="Moreoptions" />
                    </template>
                    <b-dropdown-item
                      v-if="$can('get', 'invalidations', 'cdn')"
                      @click="onDetailsClick(invalidation)"
                    >
                      Details
                    </b-dropdown-item>
                    <b-dropdown-item
                      v-if="$can('create', 'invalidations', 'cdn')"
                      @click="copyClicked(invalidation)"
                    >Copy</b-dropdown-item>
                  </b-dropdown>
                </template>
              </card>
            </template>
          </div>
          <div
            v-if="loading"
            class="mobile-loading"
          >
            <table-busy />
          </div>
          <template v-if="!loading && invalidations.length < 1">
            <paper-simple class="top-fade-in">
              <no-data
                class="text-center"
                no-data-text="No invalidations have been added yet..."
                create-text="+ Create Invalidation"
                access-page="Invalidations"
                :has-permissions="hasPermissions"
                @action="showForm = true"
              />
            </paper-simple>
          </template>
        </div>
        <div
          v-if="invalidations.length > 0"
          class="default-pagination"
        >
          <b-pagination
            v-model="currentPage"
            :total-rows="totalCount"
            :per-page="pageSize"
            first-number
            last-number
          />
          <div class="results">
            Viewing {{ first.toLocaleString() }} to {{ last.toLocaleString() }} of {{ totalCount.toLocaleString() }} Items
          </div>
        </div>
      </div>
    </div>

    <invalidation-form
      v-if="showForm"
      :id="copy ? findId() : null"
      :copy="copy"
      @close="closeForm"
      @add="invalidationAdded()"
    />
    <invalidation-view
      v-if="showView"
      :invalidation-id="findId()"
      @close="showView = false"
    />
  </div>
</template>

<script>
import Card from '@/components/card/Card.vue'
import InvalidationForm from './Form.vue'
import InvalidationView from './View.vue'

export default {
  name: 'Invalidations',
  components: {
    InvalidationForm,
    InvalidationView,
    Card,
  },
  data() {
    return {
      selected: {},
      fields: [
        {
          key: 'selected',
          label: '',
          variant: 'select',
          hide: true,
        },
        { key: 'invalidationId', isPrimary: true },
        { key: 'status', variant: 'small', isPrimary: true },
        { key: 'createdAt', label: 'Created', variant: 'time' },
        { key: 'completedAt', label: 'Completed', variant: 'time' },
      ],
      showForm: false,
      currentPage: 1,
      showView: false,
      copy: false,
      hasPermissions: true,
    }
  },
  computed: {
    invalidations() {
      return this.$store.state.cdn.invalidations
    },
    loading() {
      return this.$store.state.cdn.fetchingInvalidations
    },
    isSelected() {
      return this.selected[0]
    },
    resultInfo() {
      return this.$store.state.cdn.invalidationsResultInfo
    },
    totalCount() {
      return this.resultInfo.totalCount
    },
    pageSize() {
      return this.resultInfo.pageSize || 10
    },
    pageIndex() {
      return this.currentPage - 1
    },
    first() {
      return this.pageIndex * this.pageSize + 1
    },
    last() {
      return this.currentPage * this.pageSize < this.totalCount
        ? this.currentPage * this.pageSize
        : this.totalCount
    },
    currentBreakPoint() {
      return this.$store.getters['app/currentBreakPoint']
    },
  },
  watch: {
    currentPage() {
      this.getInvalidations()
    },
  },
  created() {
    this.id = this.$route.params.id
    this.getInvalidations()
  },
  methods: {
    getInvalidations() {
      this.$store
        .dispatch('cdn/fetchInvalidations', {
          distributionId: this.id,
          pageIndex: this.pageIndex,
          pageSize: this.pageSize,
        })
        .catch(error => {
          if (error.response?.status === 403) {
            this.hasPermissions = false
            return
          }
          this.errorToast(error.response?.data?.errors[0] || 'Something went wrong while fetching invalidations.')
        })
    },
    onRowSelected(item) {
      this.selected = item
    },
    onCopyClick() {
      this.copy = true
      this.showForm = true
    },
    copyClicked(invalidation) {
      this.selected = [invalidation]
      this.onCopyClick()
    },
    invalidationAdded() {
      this.closeForm()
      this.$refs.table.clearSelected()
      this.selected = []
    },
    findId() {
      return this.selected[0]?.invalidationId
    },
    closeForm() {
      this.copy = false
      this.showForm = false
    },
    onRefresh() {
      this.getInvalidations()
    },
    onDetailsClick(item) {
      this.selected = [item]
      this.showView = true
    },
  },
}
</script>
<style lang="scss" scoped>
.table-wrapper {
  .default-table {
    margin-top: 12px;
  }
}
</style>
